import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { bizWorkspaceAPI as bizAPI } from '../services/bizApi';
import { BizShell, useBizStyles } from './BizSetupPage';

const fmt = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function BizSplitsPage() {
  const bs = useBizStyles();
  const [collabs, setCollabs] = useState([]);
  const [splits, setSplits] = useState([]);
  const [err, setErr] = useState(null);
  const [cForm, setCForm] = useState({ name: '', email: '' });
  const [sForm, setSForm] = useState({ description: '', total_amount: '', parts: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([bizAPI.listCollaborators(), bizAPI.listSplits()]);
      setCollabs(c.data.collaborators); setSplits(s.data.splits); setErr(null);
    } catch (e) { setErr(e?.response?.data?.detail || 'Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addCollab = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await bizAPI.addCollaborator(cForm);
      toast.success('Collaborator added — share their onboarding link so they can receive payouts');
      navigator.clipboard?.writeText(data.onboarding_url);
      toast.info('Onboarding link copied to clipboard');
      setCForm({ name: '', email: '' }); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const createSplit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await bizAPI.createSplit({
        description: sForm.description, total_amount: parseFloat(sForm.total_amount),
        splits: sForm.parts.filter((p) => p.amount).map((p) => ({ collab_id: p.collab_id, amount: parseFloat(p.amount) })),
        origin_url: window.location.origin,
      });
      navigator.clipboard?.writeText(data.payment_url);
      toast.success('Split created — payment link copied. Share it with your client.');
      setSForm({ description: '', total_amount: '', parts: [] }); load();
    } catch (e2) { toast.error(e2?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const inputClass = 'w-full rounded-xl px-3 h-11 text-sm focus:outline-none transition-colors';

  return (
    <BizShell title="Collaborators & Splits" subtitle="Pay your engineer or featured artist automatically the moment a client pays you." testId="biz-splits-page">
      {err && <div className="p-4 text-sm text-[#F59E0B] mb-5" style={bs.cardStyle} data-testid="splits-error">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div className="p-5" style={bs.cardStyle}>
          <p className="text-[11px] tracking-[0.14em] uppercase font-semibold mb-4" style={{ color: bs.labelColor }}>Add a collaborator</p>
          <form onSubmit={addCollab} className="space-y-3" data-testid="collab-form">
            <input required placeholder="Name" value={cForm.name} onChange={(e) => setCForm((f) => ({ ...f, name: e.target.value }))} className={inputClass} style={bs.inputStyle} data-testid="collab-name" />
            <input type="email" required placeholder="Email" value={cForm.email} onChange={(e) => setCForm((f) => ({ ...f, email: e.target.value }))} className={inputClass} style={bs.inputStyle} data-testid="collab-email" />
            <button type="submit" disabled={busy} className="w-full h-11 rounded-xl font-bold text-black text-sm disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ background: bs.GREEN }} data-testid="collab-submit">Add collaborator</button>
          </form>
          <div className="mt-4 space-y-2">
            {collabs.map((c) => (
              <div key={c.collab_id} className="flex items-center gap-2 text-xs rounded-xl px-3 py-2.5" style={{ background: bs.t.surfaceAlt, border: `1px solid ${bs.listItemBorder}` }}>
                <span className="flex-1 truncate" style={{ color: bs.textPrimary }}>{c.name} · {c.email}</span>
                <span className="text-[10px] font-semibold" style={{ color: c.payouts_enabled ? bs.GREEN : '#F59E0B' }}>{c.payouts_enabled ? 'READY' : 'PENDING'}</span>
                {!c.payouts_enabled && (
                  <button
                    onClick={() => bizAPI.collabLink(c.collab_id).then(({ data }) => { navigator.clipboard?.writeText(data.onboarding_url); toast.success('Link copied'); })}
                    className="underline text-[11px] hover:opacity-80"
                    style={{ color: bs.textMuted }}
                  >
                    copy link
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-5" style={bs.cardStyle}>
          <p className="text-[11px] tracking-[0.14em] uppercase font-semibold mb-4" style={{ color: bs.labelColor }}>Create a split payment</p>
          <form onSubmit={createSplit} className="space-y-3" data-testid="split-form">
            <input required placeholder="What is this payment for?" value={sForm.description} onChange={(e) => setSForm((f) => ({ ...f, description: e.target.value }))} className={inputClass} style={bs.inputStyle} data-testid="split-desc" />
            <input type="number" step="0.01" min="1" required placeholder="Total amount client pays (USD)" value={sForm.total_amount} onChange={(e) => setSForm((f) => ({ ...f, total_amount: e.target.value }))} className={inputClass} style={bs.inputStyle} data-testid="split-total" />
            {collabs.map((c) => (
              <div key={c.collab_id} className="flex items-center gap-2">
                <span className="text-xs flex-1 truncate" style={{ color: bs.textMuted }}>{c.name}</span>
                <input type="number" step="0.01" min="0" placeholder="Their share"
                  value={sForm.parts.find((p) => p.collab_id === c.collab_id)?.amount || ''}
                  onChange={(e) => setSForm((f) => ({
                    ...f,
                    parts: [...f.parts.filter((p) => p.collab_id !== c.collab_id),
                      { collab_id: c.collab_id, amount: e.target.value }],
                  }))}
                  className={`${inputClass} w-32`} style={bs.inputStyle} data-testid={`split-part-${c.collab_id}`} />
              </div>
            ))}
            <button type="submit" disabled={busy} className="w-full h-11 rounded-xl font-bold text-black text-sm disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ background: bs.GREEN }} data-testid="split-submit">Create payment link</button>
          </form>
        </div>
      </div>

      <div style={bs.cardStyle} data-testid="splits-list">
        <p className="text-[11px] tracking-[0.14em] uppercase font-semibold px-5 pt-5 pb-3" style={{ color: bs.labelColor }}>Split payments</p>
        {splits.length === 0 ? (
          <p className="text-xs px-5 pb-5" style={{ color: bs.textFaint }}>None yet.</p>
        ) : splits.map((s) => (
          <div key={s.split_id} className="px-5 py-3.5 flex items-center gap-3 text-sm" style={{ borderTop: `1px solid ${bs.listItemBorder}` }}>
            <div className="flex-1 min-w-0">
              <p className="truncate" style={{ color: bs.textPrimary }}>{s.description}</p>
              <p className="text-[10px] mt-0.5" style={{ color: bs.textMuted }}>{s.payment_status}{s.transfers_processed && ' · payouts sent'}</p>
            </div>
            <p className="font-semibold tabular-nums" style={{ fontFamily: "'Bebas Neue', sans-serif", color: bs.textPrimary }}>{fmt(s.total_cents / 100)}</p>
            {s.payment_status === 'pending' && (
              <button
                onClick={() => bizAPI.splitStatus(s.split_id).then(load)}
                className="text-xs underline hover:opacity-80"
                style={{ color: bs.textMuted }}
                data-testid={`split-check-${s.split_id}`}
              >
                check
              </button>
            )}
          </div>
        ))}
      </div>
    </BizShell>
  );
}
